import { isEqual } from "lodash";

import { ApiBaseClass, OurUseQueryOptions } from "@nens/react-query-utils";

import {
  AccountsApi,
  ApiKeysApi,
  Class3dTilesApi,
  ClearlyHubIntegrationApi,
  DatasetsApi,
  EventsApi,
  FileDescriptorsApi,
  FilesApi,
  FolderTemplatesApi,
  FrontendSettingsApi,
  InstanceUsersApi,
  InvitationsApi,
  JobsApi,
  LoggingApi,
  ProcessesApi,
  ProjectsApi,
  PublicationCommentsApi,
  PublicationVersionsApi,
  PublicationsApi,
  RastersApi,
  ScenariosApi,
  StaticFilesApi,
  StylesApi,
  TenantsApi,
  ThreediSchematisationsApi,
  ThreediUserApi,
  UsersApi,
  VectorsApi,
  WorkflowsApi,
} from "./apis/index";
import { BASE_PATH, Configuration } from "./runtime";

// EXPLANATION FOR THIS SETUP
// Almost all of the requests we do to the backend require a tenant ID. This would be very inefficient to add in every request we do.
// So we save it here and then add it to every request done to the backend.

/* tslint:disable */
/* eslint-disable */
type IgnoreAny = any;

const apis = {
  AccountsApi,
  DatasetsApi,
  EventsApi,
  FileDescriptorsApi,
  FilesApi,
  FolderTemplatesApi,
  FrontendSettingsApi,
  InvitationsApi,
  JobsApi,
  LoggingApi,
  ProcessesApi,
  ProjectsApi,
  PublicationsApi,
  PublicationCommentsApi,
  PublicationVersionsApi,
  RastersApi,
  ScenariosApi,
  StylesApi,
  TenantsApi,
  ThreediSchematisationsApi,
  ThreediUserApi,
  UsersApi,
  VectorsApi,
  WorkflowsApi,
  ApiKeysApi,
  InstanceUsersApi,
  ClearlyHubIntegrationApi,
  StaticFilesApi,
  Class3dTilesApi,
} as const;

// Store for the tenant ID and AuthToken
let tenantId: string | null = null;
let accessToken: string | null = null;

const setTenantId = (id: string) => {
  tenantId = id;
};

const setAccessToken = (token: string) => {
  accessToken = token;
};

type GetQueryKeyOptions = {
  ignoreTenant?: boolean;
};

const getQueryOptions = <T>(
  queryOptions: OurUseQueryOptions<T> & GetQueryKeyOptions
): OurUseQueryOptions<T> => {
  const key = queryOptions.queryKey ?? [];
  const { ignoreTenant = false, ...actualQueryOptions } = queryOptions ?? {};

  return {
    ...actualQueryOptions,
    staleTime: 30 * 1000, // Consider fresh for 30 seconds
    structuralSharing: (oldData, newData) => (isEqual(oldData, newData) ? oldData : newData), // Use structural sharing to prevent unnecessary re-renders
    queryKey: ignoreTenant ? ["rana-api-v1", ...key] : ["rana-api-v1", tenantId, ...key],
  };
};

// Utility type to remove tenantId from the original params
type WithoutTenantId<T> = T extends { tenantId?: string } ? Omit<T, "tenantId"> : T;

// Type for the wrapped API methods
type WrappedApiMethod<T> = T extends (...args: infer A) => infer R
  ? (...args: [WithoutTenantId<A[0]>, ...(A extends [IgnoreAny, ...infer Rest] ? Rest : [])]) => R
  : never;

// Get the original API method parameter types and create a new type without tenantId
type ApiMethods<T> = {
  [K in keyof T]: WrappedApiMethod<T[K]>;
};

// Adjusted type for API instances with methods correctly typed
type ApiInstances = {
  [K in keyof typeof apis]: {
    [M in keyof InstanceType<(typeof apis)[K]>]: WrappedApiMethod<
      InstanceType<(typeof apis)[K]>[M]
    >;
  };
};

// Wrap API methods to include tenantId automatically, with instantiation inside the function
const wrapApiWithTenantId = <T extends new (...args: IgnoreAny[]) => IgnoreAny>(
  ApiClass: T,
  excludeTenantID?: boolean
): ApiMethods<InstanceType<T>> => {
  const configuration = new Configuration({
    accessToken: accessToken + "",
  });
  const apiInstance = new ApiClass(configuration);
  const wrappedApi = {} as ApiMethods<InstanceType<T>>;
  const propertyNames = Object.getOwnPropertyNames(Object.getPrototypeOf(apiInstance));

  for (const idx in propertyNames) {
    const key = propertyNames[idx];
    if (typeof apiInstance[key] === "function") {
      const originalFn = apiInstance[key] as (...args: IgnoreAny[]) => IgnoreAny;

      // Wrap the original function to inject tenantId
      (wrappedApi as IgnoreAny)[key] = ((...args: IgnoreAny[]) => {
        const [params = {}, ...restArgs] = args;

        if (excludeTenantID) {
          // Call the original function without tenantId
          return originalFn.call(apiInstance, params, ...restArgs);
        }

        if (!tenantId && !params.tenantId) {
          throw new Error("Tenant ID is not set for " + key);
        }

        // Inject tenantId into parameters
        const newParams = { tenantId, ...params } as WithoutTenantId<
          Parameters<typeof originalFn>[0]
        >;

        return originalFn.call(apiInstance, newParams, ...restArgs);
      }) as WrappedApiMethod<typeof originalFn>;
    }
  }

  return wrappedApi;
};

// Use the updated wrapApiWithTenantId to create instances and inject tenantId
const getRanaApiV1 = (excludeTenantID?: boolean) => {
  const apiInstances = Object.fromEntries(
    Object.entries(apis).map(([key, ApiClass]) => {
      return [key, wrapApiWithTenantId(ApiClass, excludeTenantID)];
    })
  ) as ApiInstances;

  return {
    ...apiInstances,
    tenantId,
    BaseApi: new ApiBaseClass(BASE_PATH, undefined, accessToken + ""),
    accessToken,
    setTenantId,
    setAccessToken,
    getQueryOptions,
  };
};

export default getRanaApiV1;
